import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Chip, Skeleton, Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import { PLANS_PATH, BUY_CREDITS_PATH } from '../utils/routes';
import { freePlanNextSteps, FREE_PLAN_ACTION } from '../utils/subscriptionCopy';
import { getPlanDisplayName } from '../utils/planUtils';
import { creditsSummaryMessage } from '../utils/creditRates';
import { useCreditBalances } from '../hooks/useCreditBalances';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import {
  deriveCancellationBanner, derivePendingChange, deriveStatusChip, CHANGE_SCHEDULED_LABEL,
} from '../utils/subscriptionState';

// ── 4.1 — the plan block in Settings, driven by real subscription data ───────
//
// WHAT WAS WRONG
// The block rendered a single chip: `userData?.plan_name || 'Basic Plan'`.
// The name itself was dynamic, but nothing else about the subscription was
// shown at all — no price, no billing interval, no renewal date, no status, and
// no indication that a cancellation was scheduled. A user on a cancelled
// subscription saw exactly what an active subscriber saw.
//
// SOURCE OF TRUTH
// GET /api/v1/users/subscription/:user_id — the EXISTING endpoint, already used
// by the Credits & Subscriptions tab. It returns the subscriptions row joined
// with its plan, which covers every field below, so no new endpoint was needed
// and there is no second definition of "what plan is this user on" to drift.
//
// Response shape consumed here (snake_case as the backend returns it — field
// names are deliberately NOT renamed to match the spec's camelCase list):
//   plan_name            string
//   plan_slug            string
//   plan_price           integer, minor units (cents)
//   status               'active' | 'cancelled' | 'paused' | 'pending_downgrade'
//   next_billing_date    ISO date — the current period end
//   cancel_at_period_end boolean
//   start_date           ISO date
//   plan_content_credits / plan_image_credits / plan_keyword_credits  integers
//   pending_plan_name    string | null — a downgrade queued for period end
//
// `null` data is not an error: it is the free/trial tier, which has no
// subscriptions row by design.

// The plans table stores minor units with no currency column, and the Stripe
// integration is USD-only today. Declared once here so switching to a
// per-plan currency later is a one-line change rather than a hunt.
const DEFAULT_CURRENCY = 'usd';

// Every plan in this product bills monthly. Read from the API if it ever starts
// sending an interval, rather than assuming forever.
const DEFAULT_INTERVAL = 'month';

// OLD: a private STATUS_PRESENTATION table of labels + colours, which is why
// this block could say "Cancels at period end" about a subscription the Credits
// tab described differently. The WORDS now come from deriveStatusChip, shared
// with that tab; only the palette lives here, keyed by the tone it returns.
const TONE_COLOURS = {
  positive: { bg: 'rgba(0,106,99,0.10)', color: '#006A63' },
  warning: { bg: '#FFF3E0', color: '#8A4B00' },
  danger: { bg: '#FDECEA', color: '#B3261E' },
  muted: { bg: '#F0F0EA', color: '#6B6F68' },
};

const formatMoney = (minorUnits, currency = DEFAULT_CURRENCY) => {
  if (minorUnits === null || minorUnits === undefined || Number.isNaN(Number(minorUnits))) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency).toUpperCase(),
    }).format(Number(minorUnits) / 100);
  } catch (_) {
    return `${(Number(minorUnits) / 100).toFixed(2)} ${String(currency).toUpperCase()}`;
  }
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Intl rather than a hand-rolled string, so the user's locale decides the
  // order of day and month instead of the developer's.
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
};

/**
 * "Moves to the Scale plan on September 2, 2026".
 *
 * The date is the point of this row — "at period end" made the user work out
 * which day that was from another line. It falls back to the period-end wording
 * only when the payload carries no usable date at all.
 *
 * "the ... plan" rather than the bare name: "Moves to Grow on September 19" reads
 * as a verb phrase — moves to grow — where "Moves to the Grow plan" can only be
 * read as the plan it names. Same construction as pendingChangeMessage(), which
 * words this change for the Credits and Manage Plans surfaces.
 */
const scheduledChangeText = ({ planName, effectiveAt }) => {
  const display = planName ? getPlanDisplayName({ name: planName }) : null;
  // getPlanDisplayName falls through to the raw database name when the plan is
  // not in its table, and those names carry a "Plan" suffix — so strip it, or an
  // unmapped plan renders "the Pro Plan plan".
  const plan = display ? String(display).trim().replace(/\s+plan$/i, '') : null;
  const when = effectiveAt ? formatDate(effectiveAt) : null;
  if (plan && when) return `Moves to the ${plan} plan on ${when}`;
  if (plan) return `Moves to the ${plan} plan at the end of your billing period`;
  if (when) return `A different plan takes effect on ${when}`;
  return 'A different plan takes effect at the end of your billing period';
};

/**
 * "Moves to the Basic plan on October 11, 2026" — for a CANCELLING plan.
 *
 * REQUESTED: a cancellation is a scheduled move to the free Basic plan, and
 * Settings now says so on the same "Scheduled change" row a downgrade uses.
 *
 * Not scheduledChangeText: that one maps the name through getPlanDisplayName,
 * which reads 'Basic' as the paid entry tier's old name and would announce a
 * move to GROW. The destination here is always the free plan, so it is written
 * out. The date goes through the same formatDate as "Access ends" above it,
 * because the two rows name one moment.
 */
const basicPlanMoveText = (effectiveAt) => {
  const when = formatDate(effectiveAt);
  return when
    ? `Moves to the Basic plan on ${when}`
    : 'Moves to the Basic plan at the end of your billing period';
};

const Row = ({ label, value, valueColor = '#1A1A1A', hint = null }) => (
  <Box sx={{ py: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#8A8F85' }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: valueColor, textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
    {/* Full width beneath the pair: this sentence is long enough that sharing
        the value's right-aligned column would wrap it into a ragged block. */}
    {hint && (
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#8A8F85', mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * @param {number|undefined} userId
 * @param {number} refreshKey  bump to force a refetch after a plan change,
 *                             cancellation, reactivation or credit purchase,
 *                             so this block never shows stale billing data.
 */
const SubscriptionSummary = ({ userId, userData = null, refreshKey = 0 }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  // The live price table, for what a balance too small for a piece still buys.
  const { pricing } = useCreditBalances(userId);

  const load = useCallback(async () => {
    if (!userId) return;
    setState((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const res = await api.getUserSubscription(userId);
      setState({ status: 'ready', data: res?.data || null, error: null });
    } catch (err) {
      // Deliberately NO fallback to a hardcoded plan. A plausible-looking wrong
      // plan is worse than a visible error: the user would believe it.
      setState({
        status: 'error',
        data: null,
        error: err?.response?.data?.error || 'We could not load your subscription just now.',
      });
    }
  }, [userId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // One allotment, whichever legacy column carries it. The server writes the
  // same pooled figure to all three; max() rather than a sum, because summing
  // them is precisely the 3x error this row used to invite.
  const includedCredits = Math.max(
    Number(state.data?.plan_content_credits) || 0,
    Number(state.data?.plan_image_credits) || 0,
    Number(state.data?.plan_keyword_credits) || 0,
  );
  // Read before the early returns below — it is a hook.
  const includedPieces = usePieceEstimate(includedCredits > 0 ? includedCredits : null);

  // ── Loading — skeletons sized to the real content, so nothing shifts ──
  if (state.status === 'loading') {
    return (
      <Box sx={{ mb: 3 }} aria-busy="true" aria-live="polite">
        <Skeleton variant="rounded" width={140} height={24} sx={{ mb: 1.25 }} />
        <Skeleton variant="text" width="100%" height={22} />
        <Skeleton variant="text" width="80%" height={22} />
      </Box>
    );
  }

  // ── Error — say so, and offer a way out ──
  if (state.status === 'error') {
    return (
      <Box sx={{ mb: 3, p: 2, border: '1px solid #F3C7C3', bgcolor: '#FDECEA', borderRadius: '12px' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#B3261E', mb: 1 }}>
          {state.error}
        </Typography>
        <Button
          onClick={load}
          size="small"
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 13, color: '#B3261E', p: 0, minWidth: 'auto' }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  const sub = state.data;

  // ── No subscription — free tier / trial. An explicit state, not a blank. ──
  //
  // MERGE (IfrahWorks) — a free-plan account is not simply "Basic plan": it is
  // either a live trial or an expired one, and those need different words and
  // different colour. Showing a flat "Basic plan" to someone whose trial has
  // ended reads as a healthy state and hides the reason their credits stopped
  // working. Derivation mirrors the Dashboard header badge, so all three
  // agree.
  if (!sub) {
    const trialExpired = userData?.trial_expired === true
      || (!!userData?.trial_ends_at && new Date(userData.trial_ends_at).getTime() < Date.now());
    const trialEnds = formatDate(userData?.trial_ends_at);

    // ── Basic plan vs a trial still running ────────────────────────────────
    // An account with no subscription is on the BASIC PLAN unless a trial is
    // actually still running. That covers both the trial that has ended and
    // the account that never had one — which used to render as two different
    // things, the second of them self-contradictory: a "Free trial" chip above
    // the words "you're on the free tier".
    const onFreePlan = trialExpired || !userData?.trial_ends_at;

    // Same source as every other status word on this screen. deriveStatusChip
    // now returns "Basic plan" for an expired trial itself, so this agrees with
    // the Credits card and the Dashboard badge rather than overriding them.
    //
    // The local answer still covers one case the helper cannot see: an account
    // with NO trial dates at all. `trialExpired` is false there, so the helper
    // says "Free trial" — which is how this line used to end up contradicting
    // itself. Whether a trial ever existed is not something the helper is told.
    const trialChip = deriveStatusChip(null, { trialExpired });
    const chipLabel = onFreePlan ? 'Basic plan' : trialChip.label;

    return (
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#8A8F85' }}>
          Current Plan:
        </Typography>
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            // Teal on the free plan: it is a plan the account is legitimately
            // on, not a fault. The orange is kept for a trial that is running
            // out, which is the case that still wants attention.
            background: onFreePlan ? '#EEF1EC' : '#E8F3F1',
            color: onFreePlan ? '#5A5F55' : '#1C7C73',
            fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, borderRadius: '8px',
          }}
        />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#8A8F85' }}>
          {/* ── What this replaces ────────────────────────────────────────
              "Upgrade to restore full access — any credits you purchased
              remain usable."

              It named no plan, offered no route to either fix, and led with a
              loss ("restore full access") rather than with what the account
              can do next. It also implied subscribing was the only way
              forward, when credit packs are sold precisely to the audience
              that does not want a subscription.

              Both routes are real links, so this is somewhere to GO rather
              than something to be told. RouterLink keeps them ordinary
              anchors — middle-click and right-click behave. */}
          {onFreePlan ? (
            freePlanNextSteps().map((seg, i) => (seg.action ? (
              <Link
                key={seg.text}
                component={RouterLink}
                to={seg.action === FREE_PLAN_ACTION.PLANS ? PLANS_PATH : BUY_CREDITS_PATH}
                underline="hover"
                sx={{ color: '#1C7C73', fontWeight: 700 }}
              >
                {seg.text}
              </Link>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`t${i}`}>{seg.text}</React.Fragment>
            )))
          ) : (
            trialEnds
              ? `No paid subscription — your trial runs until ${trialEnds}.`
              : "No paid subscription — you're on the Basic plan."
          )}
        </Typography>
      </Box>
    );
  }

  // The shared rules rather than the raw flag and a local table, so Settings
  // and the Credits page answer "is this winding down?" and "what is it called?"
  // identically — including the cases the flag alone gets wrong (already ended,
  // end date already passed).
  // OLD: const presentation = STATUS_PRESENTATION[sub.status] || …;
  //      const isCancelling = !!sub.cancel_at_period_end;
  const isCancelling = deriveCancellationBanner(sub).visible;
  // REQUESTED: "Scale · Change scheduled" while a cancellation is scheduled,
  // with the move to Basic stated on its own row below. Settings only — the
  // Credits card keeps "Set to cancel" beside its Cancellation scheduled banner
  // and Reactivate button, so deriveStatusChip itself is unchanged.
  // OLD: const statusChip = deriveStatusChip(sub);
  //      (declared above isCancelling, which it did not need)
  const statusChip = isCancelling
    ? { key: 'change_scheduled', label: CHANGE_SCHEDULED_LABEL, tone: 'warning' }
    : deriveStatusChip(sub);
  const tone = TONE_COLOURS[statusChip.tone] || TONE_COLOURS.positive;
  const pendingChange = derivePendingChange(sub);
  const price = formatMoney(sub.plan_price, sub.currency || DEFAULT_CURRENCY);
  const interval = sub.billing_interval || DEFAULT_INTERVAL;
  const periodEnd = formatDate(sub.next_billing_date);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#8A8F85' }}>
          Current Plan:
        </Typography>
        {/* MERGE (IfrahWorks) — the DB name is not the name users see:
            PLAN_DISPLAY_NAMES maps "Pro" to "Scale", "Starter" to "Optimize".
            Rendering plan_name raw made this the one place in the app that
            disagreed with the Dashboard header and the Credits tab.
            OLD: label={sub.plan_name || 'Subscription'} */}
        <Chip
          label={getPlanDisplayName({ name: sub.plan_name, slug: sub.plan_slug }) || 'Subscription'}
          size="small"
          sx={{ background: '#E8F3F1', color: '#1C7C73', fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, borderRadius: '8px' }}
        />
        {/* Cancellation outranks the raw status (deriveStatusChip handles that):
            while cancelling, `status` is still 'active' or 'trialing', so
            showing that alone would hide the fact that the plan is ending.
            OLD: label={isCancelling ? 'Cancels at period end' : presentation.label} */}
        <Chip
          label={statusChip.label}
          size="small"
          sx={{
            background: tone.bg,
            color: tone.color,
            fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, borderRadius: '8px',
          }}
        />
      </Box>

      {price && <Row label="Price" value={`${price} / ${interval}`} />}

      {periodEnd && (
        <Row
          // The same date means two different things depending on whether the
          // plan is renewing or ending. Labelling both "Next billing" is how a
          // cancelled subscription ends up looking active.
          label={isCancelling ? 'Access ends' : 'Renews on'}
          value={periodEnd}
          valueColor={isCancelling ? '#8A4B00' : '#1A1A1A'}
        />
      )}

      {/* ── The move to Basic, for a cancelling plan ──
          REQUESTED between "Access ends" and the credits row. The date is the
          period end — the same one "Access ends" shows — because that is when
          the account lands on Basic. The row below cannot appear alongside it:
          derivePendingChange stands down while a cancellation is scheduled. */}
      {isCancelling && (
        <Row
          label="Scheduled change"
          value={basicPlanMoveText(sub.next_billing_date)}
          valueColor="#8A4B00"
        />
      )}

      {/* Same derivation as the Credits page: silent for a change to the plan
          already held, for one whose date has passed, and while a cancellation
          is scheduled — the state that had Settings announcing a move to Grow
          on a Scale subscription that was also set to end.
          OLD: {sub.pending_plan_name && ( ... )} */}
      {pendingChange.visible && (
        <Row
          label="Scheduled change"
          // Two fixes here. The plan is named the way the product names it —
          // "Pro Plan" is a database string for a plan we sell as Scale — and
          // the row states the DATE instead of the vaguer "at period end".
          // getPlanDisplayName answers "Unknown Plan" for a nameless plan,
          // which is worse than not naming it at all.
          // OLD: `Moves to ${getPlanDisplayName(...)} at period end`
          value={scheduledChangeText(pendingChange)}
          valueColor="#8A4B00"
        />
      )}

      {/* ── Included monthly credits ──
          Was "220 content · 220 image · 220 keyword" — the SAME allotment
          printed three times, because the server sets all three legacy columns
          to the one pooled figure. Read as a list of three it invites the
          conclusion that the plan grants 660 credits a month.

          There is one pool, so there is one number. The sentence underneath is
          the same helper the Credits page, Overview and Websites card use, so
          the four surfaces cannot translate one balance into four different
          descriptions of what it buys. */}
      {includedCredits > 0 && (
        <Row
          label="Included monthly credits"
          value={`${includedCredits.toLocaleString()} credits`}
          hint={creditsSummaryMessage(includedCredits, includedPieces, pricing)}
        />
      )}
    </Box>
  );
};

export default SubscriptionSummary;
